import { createAction, handleActions } from "redux-actions"
import { tabularDataRowsOnPage } from "../../config/settings"
import { endOfDay, startOfDay } from "date-fns"

// Default state
const defaultState = {
  data: [],
  filters: [],
  currentPage: 1,
  totalPages: 1,
  searchQuery: null,
  sortedBy: null,
  sortedAsc: true,
  pendingRequests: 0,
  startDate: new Date(),
  endDate: new Date(),
  type: "",
}

export const getTabularReducerInfo = (
  actionPrefix,
  refresh,
  buildRefreshActionParameters = () => ({}),
  specificDefaultState = {},
  specificReducers = {},
) => {
  const actions = {
    clear: createAction(actionPrefix + "_CLEAR"),
    setData: createAction(actionPrefix + "_SET_DATA"),
    edit: createAction(actionPrefix + "_EDIT_DATA"),
    setFilters: createAction(actionPrefix + "_SET_FILTERS"),
    setCurrentPage: createAction(actionPrefix + "_SET_CURRENT_PAGE"),
    setTotalPages: createAction(actionPrefix + "_SET_TOTAL_PAGES"),
    setSearchQuery: createAction(actionPrefix + "_SET_SEARCH_QUERY"),
    setTotalCount: createAction(actionPrefix + "_SET_TOTAL_COUNT"),
    setType: createAction(actionPrefix + "_SET_TYPE"),
    setSortInfo: createAction(
      actionPrefix + "_SET_SORT_INFO",
      (column, asc) => ({ column, asc }),
    ),
    setLoading: createAction(actionPrefix + "_SET_LOADING", (flag) =>
      flag ? 1 : -1,
    ),
    setDateRange: createAction(
      actionPrefix + "_SET_DATE_RANGE",
      (startDate, endDate) => ({ startDate, endDate }),
    ),
  }

  const mergedDefaultState = { ...defaultState, ...specificDefaultState }
  const fun = function (state, { payload: { startDate, endDate } }) {
    return { ...state, startDate, endDate }
  }
  const reducer = handleActions(
    {
      // eslint-disable-next-line no-empty-pattern
      [actions.clear]: (state, {}) => ({ ...mergedDefaultState }),
      [actions.setData]: (state, { payload }) => ({ ...state, data: payload }),
      [actions.setType]: (state, { payload }) => ({ ...state, type: payload }),
      [actions.edit]: (state, { payload }) => ({ ...state }),
      [actions.setFilters]: (state, { payload }) => ({
        ...state,
        currentPage: 1,
        totalPages: 1,
        filters: payload,
      }),
      [actions.setCurrentPage]: (state, { payload }) => ({
        ...state,
        currentPage: payload,
      }),
      [actions.setTotalPages]: (state, { payload }) => ({
        ...state,
        totalPages: payload,
      }),
      [actions.setTotalCount]: (state, { payload }) => ({
        ...state,
        totalCount: payload,
      }),
      [actions.setSearchQuery]: (state, { payload }) => ({
        ...state,
        currentPage: 1,
        totalPages: 1,
        searchQuery: payload,
      }),
      [actions.setSortInfo]: (state, { payload: { column, asc } }) => ({
        ...state,
        currentPage: 1,
        sortedBy: column,
        sortedAsc: asc,
      }),
      [actions.setLoading]: (state, { payload }) => ({
        ...state,
        pendingRequests: state.pendingRequests + payload,
      }),
      [actions.setDateRange]: fun,
      ...specificReducers,
    },
    mergedDefaultState,
  )

  const thunks = {
    openPage: (page) => {
      return (dispatch, getState) => {
        dispatch(actions.setCurrentPage(page))
        dispatch(refresh(buildRefreshActionParameters(getState)))
      }
    },

    sort: (column, asc) => {
      return (dispatch, getState) => {
        dispatch(actions.setSortInfo(column, asc))
        dispatch(refresh(buildRefreshActionParameters(getState)))
      }
    },

    search: (query) => {
      return (dispatch, getState) => {
        dispatch(actions.setSearchQuery(query))
        dispatch(refresh(buildRefreshActionParameters(getState)))
      }
    },

    setDateRange: (startDate, endDate) => {
      return (dispatch, getState) => {
        dispatch(actions.setDateRange(startDate, endDate))
        dispatch(refresh(buildRefreshActionParameters(getState)))
      }
    },

    clear: () => {
      return (dispatch, getState) => {
        dispatch(actions.clear())
      }
    },

    filter: (filters) => (dispatch, getState) => {
      dispatch(actions.setFilters(filters))
      dispatch(refresh(buildRefreshActionParameters(getState)))
    },
  }

  return { actions, reducer, thunks }
}

export const downloadWithFilters = (storeId, state) => {
  const { filters, searchQuery } = state
  const rangeFilters = {}
  const valueFilters = {}
  filters.map((f) => {
    if (f.rangeFrom && f.rangeTo) {
      rangeFilters[f.column + "Range"] = [f.rangeFrom, f.rangeTo]
    } else if (!f.rangeFrom) {
      rangeFilters[f.column + "Range"] = ["0", f.rangeTo]
    } else if (!f.rangeTo) {
      rangeFilters[f.column + "Range"] = [f.rangeFrom, "999999999"]
    } else {
      return null
    }
    if (f.values) {
      valueFilters[f.column + "Values"] = f.values
    }
  })
  return {
    storeId,
    search: searchQuery,
    ...rangeFilters,
    ...valueFilters,
  }
}

export const buildRefreshParameters = (storeId, state) => {
  const {
    filters,
    startDate,
    endDate,
    sortedBy,
    sortedAsc,
    searchQuery,
    type,
  } = state
  const rangeFilters = {}
  const valueFilters = {}
  filters.map((f) => {
    if (f.rangeFrom && f.rangeTo) {
      rangeFilters[f.column + "Range"] = [f.rangeFrom, f.rangeTo]
    } else if (!f.rangeFrom) {
      rangeFilters[f.column + "Range"] = ["0", f.rangeTo]
    } else if (!f.rangeTo) {
      rangeFilters[f.column + "Range"] = [f.rangeFrom, "999999999"]
    } else {
      return null
    }
    if (f.values) {
      valueFilters[f.column + "Values"] = f.values
    }
  })
  const sort =
    sortedBy === null ? null : sortedBy + " " + (sortedAsc ? "asc" : "desc")

  return {
    storeId,
    page: state.currentPage,
    count: tabularDataRowsOnPage,
    search: searchQuery,
    sort,
    from: startDate ? startOfDay(startDate).toISOString() : null,
    to: endDate ? endOfDay(endDate).toISOString() : null,
    ...rangeFilters,
    ...valueFilters,
    type,
  }
}
